/*
 * File: kml.ts
 * Project: sso-frontend
 * File Created: Tuesday, 24th May 2022 5:58:01 pm
 * Author: Steven Chiffe (steven.chiffe@alyce.fr)
 * -----
 * Last Modified: Wednesday, 31st August 2022 3:57:17 pm
 * Modified By: Steven Chiffe (steven.chiffe@alyce.fr>)
 * -----
 * Copyright Py-Factory - 2022
 */

/**
 * Type that represent a KmlFolder.
 *
 * @type
 */
export type KmlFolders = {
  name: string;
  obj?: Array<KmlContent>;
};

/**
 * Type that represent the content of a Kml Folder .
 *
 * @type
 */
export type KmlContent = {
  name: string;
  type: string;
  locations: Array<number>;
};

class Kml {
  /**
   * Describe the xml content of the file
   */
  private xml_content: Document | undefined;

  /**
   * Document xml element
   */
  private document: Element | undefined;

  /**
   * @param  {File} privatereadonlyfile
   */
  constructor(private readonly file: File) {
    this.verifyFile();
  }

  /**
   * Transform uploaded xml file into dom tree object
   *
   * @returns Promise<void>
   */
  public xmlToDom = async (): Promise<void> => {
    const text = await this.readFileContent();
    this.xml_content = new DOMParser().parseFromString(text, "text/xml");
    this.document = this.xml_content.lastChild?.children[0] as Element;
    if (!this.document) throw new Error("Error with kml document");
  };

  /**
   * Transform Actual dom representation of the xml file into a KmlFolders json.
   *
   * @returns Array<KmlFolders>
   */
  public domToJson = (): Array<KmlFolders> => {
    try {
      const folders = (this.document as Element).querySelectorAll("Folder");
      return this.parseFolders(folders, []);
    } catch (e) {
      return [];
    }
  };

  /**
   * Parse folders Element to transform it in KmlFolders array
   *
   * @param  {NodeListOf<Element>} folders
   * @param  {Array<KmlFolders>} kml_projects
   * @returns Array<KmlFolders>
   */
  private parseFolders = (folders: NodeListOf<Element>, kml_projects: Array<KmlFolders>): Array<KmlFolders> => {
    folders.forEach((folder) => {
      kml_projects.push({ name: folder.children[0].innerHTML.replace("\n", "").trim(), obj: [] });
      const placemarks = folder.querySelectorAll("Placemark");
      placemarks.forEach((placemark) => {
        const name = placemark.querySelector("name");
        const data_type =
          placemark.querySelector("Point") ||
          placemark.querySelector("Polygon") ||
          placemark.querySelector("LineString");
        if (data_type && name && data_type.querySelector("coordinates")) {
          const coordinates = data_type.querySelector("coordinates");
          const kml_project = kml_projects.find((project) => project.name === folder.children[0].innerHTML);
          kml_project?.obj?.push({
            name: name.innerHTML.replaceAll("\n", ""),
            type: data_type.nodeName,
            locations: this.transformCoordinates(coordinates?.innerHTML as string)
          });
        }
      });
    });
    return kml_projects;
  };

  /**
   *  Parse and transform a coordinates string into a clean array of number.
   *
   * @param  {string} coordinates
   * @returns Array<number>
   */
  private transformCoordinates(coordinates: string): Array<number> {
    const a = coordinates
      .trim()
      .replaceAll("\n", "")
      .trim()
      .split(" ")
      .filter((b) => b)
      .map((coordinate) => coordinate.split(",").slice(0, 2).join())
      .map((coordinate) => coordinate.split(","))
      .flat()
      .map((value) => parseFloat(value));
    return a;
  }

  /**
   * Read the content of the kml file
   *
   * @returns Promise<string>
   */
  private readFileContent = async (): Promise<string> => this.file.text();

  /**
   * Verify the integrity of the file
   *
   * @returns void
   */
  private verifyFile = (): void => {
    if (!this.file) throw new Error("No file uploaded!");

    if (!this.file.type.includes("vnd.google-earth.kml") && !this.file.name.match(/\.kml$/)) {
      throw new Error("Invalid file type! The file must be a KML.");
    }
  };
}

export default Kml;
